// ============================================================================
// mentions.ts — SSoT упоминаний «@» на КЛИЕНТЕ (чат).
//
// Серверный контракт: упоминание в тексте сообщения — «@login» (разбор на
// сервере по логину). Клиент НЕ дублирует разбор сервера, его задачи:
//   1) автодополнение в поле ввода (поиск активного «@префикса» и вставка);
//   2) подсветка упоминаний участников в тексте сообщений;
//   3) БЕЗОПАСНОЕ чтение счётчика непрочитанных упоминаний диалога
//      (mention_count приходит от сервера; поле может появиться позже —
//      читаем по имени, без any, отсутствие поля = 0).
// ============================================================================

/** Символы, допустимые в префиксе поиска («@ив» / «@ivan.p»). */
const QUERY_CHAR_RE = /^[A-Za-zА-Яа-яЁё0-9_.-]*$/;
/** Границы, после которых «@» начинает упоминание (не часть слова/email/url). */
const MENTION_BOUNDARY = new Set([' ', '\t', '\n', '(', '[', '{', '<', '«', '"', "'"]);
const MAX_QUERY = 64;
const DEFAULT_LIMIT = 8;

export interface MentionToken {
  /** индекс «@» в тексте */
  start: number;
  /** позиция каретки (конец префикса) */
  end: number;
  /** префикс после «@» (может быть пустым — сразу после ввода «@») */
  query: string;
}

/** Активное упоминание под кареткой, иначе null (строгие границы — email/url не трогаем). */
export function mentionQueryAt(text: string, caret: number): MentionToken | null {
  const value = String(text ?? '');
  const pos = Math.max(0, Math.min(Number.isFinite(caret) ? caret : value.length, value.length));
  const at = value.lastIndexOf('@', pos - 1);
  if (at < 0) return null;
  const prev = at > 0 ? value[at - 1] : '';
  if (prev && !MENTION_BOUNDARY.has(prev)) return null;
  const query = value.slice(at + 1, pos);
  if (query.length > MAX_QUERY) return null;
  if (!QUERY_CHAR_RE.test(query)) return null;
  return { start: at, end: pos, query };
}

/** Заменить активный «@префикс» на «@login » (канонический вид для сервера). */
export function applyMention(text: string, token: MentionToken, login: string): { text: string; caret: number } {
  const insert = `@${login} `;
  const value = String(text ?? '');
  const next = value.slice(0, token.start) + insert + value.slice(token.end);
  return { text: next, caret: token.start + insert.length };
}

export interface MentionPerson {
  id: string;
  name: string;
  /** логин участника (по нему сервер разбирает упоминание) */
  login?: string;
}

export interface MentionCandidate {
  id: string;
  name: string;
  login: string;
}

/**
 * Кандидаты автодополнения: УЧАСТНИКИ диалога с непустым логином,
 * фильтр по префиксу логина/имени, сначала совпадения по логину, до limit.
 * Пустой memberIds — без фильтра по участникам (источник ещё не получен).
 */
export function mentionCandidates(
  people: readonly MentionPerson[],
  memberIds: readonly string[],
  query: string,
  limit = DEFAULT_LIMIT,
): MentionCandidate[] {
  const members = memberIds.length > 0 ? new Set(memberIds) : null;
  const q = String(query ?? '').trim().toLowerCase();
  const byLogin: MentionCandidate[] = [];
  const byName: MentionCandidate[] = [];
  const seen = new Set<string>();
  for (const p of people) {
    const login = (p.login || '').trim();
    if (!login) continue;
    if (members && !members.has(p.id)) continue;
    const key = login.toLowerCase();
    if (seen.has(key)) continue;
    const name = p.name || login;
    if (q) {
      const loginHit = key.startsWith(q);
      const nameHit = name.toLowerCase().includes(q);
      if (!loginHit && !nameHit) continue;
      if (loginHit) byLogin.push({ id: p.id, name, login });
      else byName.push({ id: p.id, name, login });
    } else {
      byLogin.push({ id: p.id, name, login });
    }
    seen.add(key);
  }
  const sortByName = (a: MentionCandidate, b: MentionCandidate) => a.name.localeCompare(b.name, 'ru');
  byLogin.sort(sortByName);
  byName.sort(sortByName);
  return [...byLogin, ...byName].slice(0, Math.max(1, limit));
}

/** «@login» в тексте (граница — начало строки/пробел/открывающая скобка/кавычка). */
const MENTION_TEXT_RE = /(^|[\s([{<«"'])@([A-Za-z0-9_.-]+)/g;

export interface MentionSegment {
  text: string;
  mention: boolean;
  login?: string;
}

/** Разбор текста на сегменты: упоминания известных участников — отдельно. */
export function mentionSegments(text: string, knownLogins: ReadonlySet<string>): MentionSegment[] {
  const value = String(text ?? '');
  const out: MentionSegment[] = [];
  let last = 0;
  MENTION_TEXT_RE.lastIndex = 0;
  let m = MENTION_TEXT_RE.exec(value);
  while (m) {
    const boundary = m[1] || '';
    const login = m[2] || '';
    const start = m.index + boundary.length;
    if (login && knownLogins.has(login.toLowerCase())) {
      if (start > last) out.push({ text: value.slice(last, start), mention: false });
      out.push({ text: `@${login}`, mention: true, login });
      last = start + login.length + 1;
    }
    m = MENTION_TEXT_RE.exec(value);
  }
  if (last < value.length) out.push({ text: value.slice(last), mention: false });
  return out;
}

// ---------- Безопасное чтение серверных полей (proto может отставать) ----------

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/** Непрочитанные упоминания диалога; поля нет (старый proto) → 0 (бейджа нет). */
export function mentionCountOf(conversation: unknown): number {
  const rec = asRecord(conversation);
  if (!rec) return 0;
  const raw = rec.mentionCount ?? rec.mention_count ?? rec.unreadMentions ?? rec.unread_mentions;
  const n = Number(raw ?? 0);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Меня упомянули в событии упоминаний/нового сообщения (payload шины чата). */
export function payloadMentionsMe(payload: unknown, myId: string): boolean {
  const rec = asRecord(payload);
  if (!rec || !myId) return false;
  const lists = [rec.mentions, rec.mention_ids, rec.mentionIds, rec.mentioned_user_ids];
  for (const raw of lists) {
    if (!Array.isArray(raw)) continue;
    for (const item of raw) {
      if (String(item) === myId) return true;
      // Серверная форма chat.message.mention.*: mentions=[{entity_id, login}]
      if (item && typeof item === 'object') {
        const entry = item as Record<string, unknown>;
        const id = entry.entity_id ?? entry.entityId ?? entry.id ?? entry.user_id ?? entry.userId;
        if (id !== undefined && String(id) === myId) return true;
      }
    }
  }
  return false;
}
